import { getTableColumns } from "drizzle-orm";
import { index, pgEnum, pgTable, text, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import z from "zod";

import {
  applicationTypeEnum,
  applicationTypeEnumSchema,
} from "./applicationsSchema";
import { householdMembersTable } from "./householdMembersSchema";
import { customJsonb, customTimestamp } from "./utils/types";

export const coverageCheckStatusEnumSchema = z.enum([
  "PENDING",
  "COMPLETE",
  "CANCELLED",
]);

export const coverageChecksStatusEnum = pgEnum(
  "coverage_check_status_enum",
  coverageCheckStatusEnumSchema.enum,
);

export const coverageStatusEnumSchema = z.enum([
  "CHECKING",
  "UNKNOWN",
  "NOT_COVERED",
  "COVERED",
]);

export const coverageStatusEnum = pgEnum(
  "coverage_status_enum",
  coverageStatusEnumSchema.enum,
);

export type CoverageStatusEnumType = z.infer<typeof coverageStatusEnumSchema>;

const coveragePlanSchema = z.object({
  name: z.string(),
});

export type CoveragePlanType = z.infer<typeof coveragePlanSchema>;

const coveragePrimaryCareProviderSchema = z.object({
  name: z.string(),
});

export type CoveragePrimaryCareProviderType = z.infer<
  typeof coveragePrimaryCareProviderSchema
>;

export const coverageChecksTable = pgTable(
  "coverage_checks",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    householdMemberId: uuid("household_member_id")
      .references(() => householdMembersTable.id, { onDelete: "cascade" })
      .notNull(),
    pointcareRequestId: uuid("pointcare_request_id"),
    pointcareReferenceId: text("pointcare_reference_id").unique().notNull(),
    applicationType: applicationTypeEnum("application_type").notNull(),
    workspaceId: text("workspace_id").notNull(),
    payerId: text("payer_id").notNull(),
    target: text("target").notNull(),
    coverageCheckStatus: coverageChecksStatusEnum("coverage_check_status")
      .notNull()
      .default("PENDING"),
    checkCompletionDate: customTimestamp("check_completion_date"),
    coverageStatus: coverageStatusEnum("coverage_status")
      .notNull()
      .default("CHECKING"),
    coverageEndDate: customTimestamp("coverage_end_date"),
    aidCodes: text("aid_codes").notNull().array().default([]),
    plan: customJsonb<CoveragePlanType>("plan"),
    primaryCareProvider: customJsonb<CoveragePrimaryCareProviderType>(
      "primary_care_provider",
    ),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    index("coverage_checks_household_member_id_idx").on(
      table.householdMemberId,
    ),
    index("coverage_checks_target_idx").on(table.target),
    index("coverage_checks_application_type_idx").on(table.applicationType),
    index("coverage_checks_created_at_idx").on(table.createdAt),
  ],
);

export const coverageCheckSchema = createSelectSchema(coverageChecksTable, {
  applicationType: applicationTypeEnumSchema,
  coverageCheckStatus: coverageCheckStatusEnumSchema,
  coverageStatus: coverageStatusEnumSchema,
  plan: coveragePlanSchema.nullable(),
  primaryCareProvider: coveragePrimaryCareProviderSchema.nullable(),
});

export type CoverageCheckType = z.infer<typeof coverageCheckSchema>;

export const insertCoverageCheckSchema = createInsertSchema(
  coverageChecksTable,
  {
    applicationType: applicationTypeEnumSchema,
  },
).omit({
  coverageCheckStatus: true,
  coverageStatus: true,
  target: true,
  aidCodes: true,
  plan: true,
  primaryCareProvider: true,
  checkCompletionDate: true,
  coverageEndDate: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertCoverageCheckType = z.infer<typeof insertCoverageCheckSchema>;

export const updateCoverageCheckSchema = createInsertSchema(
  coverageChecksTable,
  {
    coverageCheckStatus: coverageCheckStatusEnumSchema,
    plan: coveragePlanSchema.nullable(),
    coverageStatus: coverageStatusEnumSchema,
    primaryCareProvider: coveragePrimaryCareProviderSchema.nullable(),
    aidCodes: z.array(z.string().min(1)),
  },
)
  .omit({
    id: true,
    householdMemberId: true,
    applicationType: true,
    workspaceId: true,
    payerId: true,
    target: true,
    pointcareReferenceId: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial();

export type UpdateCoverageCheckType = z.infer<typeof updateCoverageCheckSchema>;

export const createCoverageTarget = ({
  workspaceId,
  payerId,
}: {
  workspaceId: string;
  payerId: string;
}) => `${workspaceId}+${payerId}`;

export const coverageChecksTableColumns = getTableColumns(coverageChecksTable);
